/**
 * Frozen catalog of app module metadata for access, routes, and preload.
 *
 * Built from {@link registryEntries} in `appModuleDefinitions.js`. Icons are **not**
 * hydrated here — resolve `sidebar.iconName` at UI boundaries via `appModuleIcons.js`.
 *
 * @module modules/appModuleCatalog
 * @see docs/Modules/module-access.md
 */

import { registryEntries } from "./appModuleDefinitions";
import { normalizeAppModuleKey } from "./appModuleKeys";

/**
 * Deep-freeze one catalog row so consumers cannot mutate nested arrays/objects.
 *
 * @param {Object} entry
 * @returns {Readonly<Object>}
 */
function freezeEntry(entry) {
  return Object.freeze({
    ...entry,
    routePaths: Object.freeze([...(entry.routePaths || [])]),
    access: Object.freeze({
      ...entry.access,
      deniedRoles: Object.freeze([...(entry.access?.deniedRoles || [])]),
      requiredRoles: Object.freeze([...(entry.access?.requiredRoles || [])]),
    }),
    sidebar: Object.freeze({
      ...entry.sidebar,
      matchPaths: Object.freeze([...(entry.sidebar?.matchPaths || [])]),
    }),
    routing: Object.freeze({ ...(entry.routing || {}) }),
    i18n: entry.i18n
      ? Object.freeze({
          ...entry.i18n,
          namespaces: Object.freeze([...(entry.i18n.namespaces || [])]),
          routeOverrides: Object.freeze(
            (entry.i18n.routeOverrides || []).map((override) =>
              Object.freeze({
                ...override,
                namespaces: Object.freeze([...(override.namespaces || [])]),
              })
            )
          ),
        })
      : undefined,
    assignmentOption: entry.assignmentOption
      ? Object.freeze({ ...entry.assignmentOption })
      : undefined,
  });
}

/** Map of module key → frozen config. Prefer {@link getAppModuleConfig} for loose lookups. */
export const APP_MODULE_CATALOG = Object.freeze(
  registryEntries.reduce((acc, entry) => {
    acc[entry.key] = freezeEntry(entry);
    return acc;
  }, {})
);

/**
 * @returns {Readonly<Object>[]} frozen module configs in registry order
 */
export function getModuleCatalogEntries() {
  return Object.values(APP_MODULE_CATALOG);
}

/**
 * Lookup a catalog entry by canonical or case-insensitive module key.
 *
 * @param {string} moduleKey
 * @returns {Readonly<Object>|undefined}
 */
export function getAppModuleConfig(moduleKey) {
  const normalizedKey = normalizeAppModuleKey(moduleKey);
  return normalizedKey ? APP_MODULE_CATALOG[normalizedKey] : undefined;
}
